<template>
	<StandardModal ref="modal">
		<h1 class="text-2xl font-serif">{{ t('you_have_an_unfinished_reservation') }}</h1>

		<p class="my-4">{{ t('finish_it_or_start_over') }}</p>

		<NoteBlock v-if="notice" variant="notice">{{ notice }}</NoteBlock>

		<NoteBlock v-for="(reason, index) in reasons" :key="index" variant="error" class="mt-4">{{ reason }}</NoteBlock>

		<template #footer>
			<div class="flex flex-col lg:flex-row gap-4 justify-between items-center">
				<ActionButton variant="light" @click="restart">
					{{ t('new_reservation') }}
				</ActionButton>

				<ActionButton @click="resume">
					{{ t('finish_reservation') }}
				</ActionButton>
			</div>
		</template>
	</StandardModal>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import ActionButton from '@/components/ActionButton.vue';
import NoteBlock from '@/components/NoteBlock.vue';
import StandardModal from '@/components/StandardModal.vue';
import { useI18n } from 'vue-i18n';

export interface Props {
  /** Shown when the reservation is not exactly what the guest left behind, for example a changed price. */
  notice?: string
  /** What is no longer bookable about it, so the guest sees that before choosing. */
  reasons?: string[]
}

const { t } = useI18n();

withDefaults(defineProps<Props>(), {
  notice: '',
  reasons: () => [],
});

const modal = ref<InstanceType<typeof StandardModal>>();

const emit = defineEmits<{
  (e: 'resume'): void
  (e: 'restart'): void
}>();

const openModal = () => {
  modal.value?.openModal();
};

const closeModal = () => {
  modal.value?.closeModal();
};

const resume = () => {
  closeModal();
  emit('resume');
};

const restart = () => {
  closeModal();
  emit('restart');
};

defineExpose({
  openModal,
  closeModal,
});
</script>
